import { Autocomplete, Checkbox, Chip, TextField } from '@mui/material';
import { County } from '../../types/County.type';

interface DatasetsMultiSelectProps {
  counties: County[];
  value?: number[];
  lockedCountyId?: number;
  onChange: (value: number[]) => void;
  label?: string;
  fullWidth?: boolean;
}

function DatasetsMultiSelect({
  counties,
  value,
  lockedCountyId,
  onChange,
  label = 'Datasets',
  fullWidth,
}: DatasetsMultiSelectProps) {
  // Preselect the locked county if it's not already included in the value
  const selectedDatasetIds =
    lockedCountyId && !value?.includes(lockedCountyId) ? [...(value ?? []), lockedCountyId] : (value ?? []);

  const selectedCounties = selectedDatasetIds
    .map((id) => counties.find((county) => county.id === id))
    .filter((county): county is County => Boolean(county));

  const handleChange = (_event: React.SyntheticEvent, nextCounties: County[]) => {
    const nextDatasetIds = nextCounties.map((county) => county.id);

    // Maintain user's county selected
    const updatedDatasetIds =
      lockedCountyId && !nextDatasetIds.includes(lockedCountyId) ? [...nextDatasetIds, lockedCountyId] : nextDatasetIds;

    onChange(updatedDatasetIds);
  };

  return (
    <Autocomplete
      multiple
      disableCloseOnSelect
      fullWidth={fullWidth}
      options={counties}
      value={selectedCounties}
      onChange={handleChange}
      getOptionLabel={(county) => county.name}
      isOptionEqualToValue={(option, selected) => option.id === selected.id}
      getOptionDisabled={(county) => county.id === lockedCountyId}
      renderOption={(props, county, { selected }) => (
        <li {...props} key={county.id}>
          <Checkbox size='small' checked={selected} disableRipple sx={{ padding: '4px 9px 4px 0px' }} />
          {county.name}
        </li>
      )}
      renderValue={(tagValue, getItemProps) =>
        tagValue.map((county, index) => {
          const { key, ...tagProps } = getItemProps({ index });
          return (
            <Chip
              {...tagProps}
              key={key}
              size='small'
              label={county.name}
              onDelete={county.id === lockedCountyId ? undefined : tagProps.onDelete}
            />
          );
        })
      }
      renderInput={(params) => <TextField {...params} label={label} />}
      slotProps={{
        popper: {
          sx: { '& .MuiAutocomplete-option': { fontSize: '14px' } },
        },
      }}
      sx={{ '& .MuiInputLabel-root': { fontSize: '14px' } }}
    />
  );
}

export default DatasetsMultiSelect;
